// apps/tmg-web/components/sitewide-header.tsx
"use client";

import * as React from "react";
import Link from "next/link";
import { Icons } from "./icons";
import { siteConfig } from "@/site.config";
import { ButtonLink } from "./ui/button-link";
import { Menu, X } from "lucide-react";

export interface NavLink {
  href: string;
  label: string;
}

interface SiteWideHeaderProps {
  navLinks?: NavLink[];
  cta?: {
    href: string;
    label: string;
    variant?: "highlight" | "default" | "outline" | "link";
  };
}

export default function SiteWideHeader({ navLinks, cta }: SiteWideHeaderProps) {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const nav = navLinks || [
    { href: "/about", label: "About" },
    { href: "/approach", label: "Approach" },
    { href: "/sectors", label: "Sectors" },
    { href: "/why-tmg", label: "Why TMG" },
    { href: "/contact", label: "Contact" },
  ];

  const ctaProps = cta || {
    href: "/contact",
    label: "Partner With Us",
    variant: "highlight" as const,
  };

  const title = siteConfig.name;

  return (
    <header className="w-full border-b bg-background z-50 relative">
      <div className="max-w-6xl mx-auto px-4 py-6 flex justify-between items-center">
        <Link
          href="/"
          className="flex items-center space-x-2"
          aria-label={`${title} home`}
        >
          <Icons.logo className="w-20 text-primary" />
          <span className="font-bold text-xl text-primary">{title}</span>
        </Link>

        {/* Desktop nav */}
        <nav
          className="hidden md:flex space-x-6 items-center"
          aria-label="Primary"
        >
          {nav.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="hover:text-highlight text-base font-medium"
            >
              {item.label}
            </Link>
          ))}
          <ButtonLink href={ctaProps.href} variant={ctaProps.variant} size="lg">
            {ctaProps.label}
          </ButtonLink>
        </nav>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          className="md:hidden text-foreground focus:outline-none"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {menuOpen && (
        <div className="md:hidden px-4 pb-6 space-y-4" aria-label="Mobile">
          {nav.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="block text-sm font-medium text-foreground hover:text-highlight"
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <ButtonLink
            href={ctaProps.href}
            variant={ctaProps.variant}
            size="lg"
            className="w-full"
            onClick={() => setMenuOpen(false)}
          >
            {ctaProps.label}
          </ButtonLink>
        </div>
      )}
    </header>
  );
}
